import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock } from 'lucide-react';
import { TopNavigation } from './TopNavigation';
import { ProgressGridModal } from './ProgressGridModal';
import { MessageCenter } from './MessageCenter';
import { DiscoveryQuestionsPanel } from './DiscoveryQuestionsPanel';
import { EscalationInbox } from './EscalationInbox';
import { EngagementSwitcher } from './EngagementSwitcher';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { saveGuidedAnswer } from '../utils/discoveryGridApi';
import { categoryLabel } from '../utils/discoveryGrid';
import { preferredEngagementIdHint, writeFocusedEngagementPreference } from '../utils/focusedEngagement';
import {
  firstNameOf,
  formatMinutes,
  greetingFor,
  initialsOf,
  lockedCategories,
  overdueSummary,
  percentOf,
  URGENCY_LABELS,
  workSummary,
  type CategoryCard,
  type EscalationCounts,
  type HealthTone,
  type HomeEvent,
  type HomeHealth,
  type HomeProgress,
  type HomeStats,
  type Milestone,
  type NextStep,
  type StatusNote,
  type AssignedRow,
  type Urgency,
  type WorkRow,
} from '../utils/clientHome';

/**
 * Client home (approved artboard: Design canvas 3wtNAjBx8wWrAdkyzgqFqt, project/Main.dc.html).
 *
 * One page answering, in order: what do you need from me, where are we, who do I talk to,
 * and what was done lately. Data comes from GET /client/home/:companyId (clientHomeRoutes.ts);
 * every row's button opens a surface the client can act on.
 */

interface TeamMember {
  userId: string;
  name: string;
  email: string;
  roleLabel: string;
}

interface HomeEngagementSummary {
  id: string;
  name: string | null;
  status: string | null;
}

/** The Journey dashboard's AI-written banner (server journeyMetadata.tsx). */
interface JourneyBanner {
  title: string;
  description: string;
  solutionType?: string;
  categoryNoun?: string;
}

interface ClientHomePayload {
  company: { id: string; name: string };
  firmName: string | null;
  engagement: HomeEngagementSummary | null;
  /** Every engagement on the company; two or more draw the switcher (RESTORE_PLAN R2.1). */
  engagements?: HomeEngagementSummary[];
  work: WorkRow[];
  /** The engine's next steps; the first is the starting point (same as the onboarding's last step). */
  nextSteps?: NextStep[];
  /** Unread messages for the viewer (what the Control Center showed as a badge). */
  messages?: { unread: number };
  progress: HomeProgress;
  /** The Control Center's overview and the Journey dashboard's cards (RESTORE_PLAN R2.1). */
  stats?: HomeStats;
  health?: HomeHealth;
  milestones?: Milestone[];
  categories?: CategoryCard[];
  escalations?: EscalationCounts;
  journey?: JourneyBanner | null;
  statusNote: StatusNote | null;
  team: TeamMember[];
  recent: HomeEvent[];
}

interface ClientHomeProps {
  companyId: string;
  companyName: string;
  accessToken: string;
  currentUser: any;
  onLogout: () => void;
  /** Open Guided Mode for a category (ClientDashboardNew.handleStartCategory). */
  onStartCategory: (category: string) => void;
  /**
   * A firm user's preview of the client's home (RESTORE_PLAN R2.6): the same page, every action
   * turned off (Answer, Snooze, Mark done, Continue, the grid, messages, the SOW approval link).
   */
  readOnly?: boolean;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

function shortDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Monday" within the last week, otherwise "Oct 24". */
function relativeDay(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return shortDate(iso);
}

function waitingFor(iso?: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 1) return 'asked today';
  return `waiting ${days} day${days === 1 ? '' : 's'}`;
}

/** "assigned today" / "assigned 3 days ago" / "assigned 2 weeks ago". */
function assignedAgo(iso?: string | null): string {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (!Number.isFinite(days) || days < 1) return 'assigned today';
  if (days === 1) return 'assigned yesterday';
  if (days < 7) return `assigned ${days} days ago`;
  if (days < 30) { const w = Math.floor(days / 7); return `assigned ${w} week${w === 1 ? '' : 's'} ago`; }
  return `assigned ${shortDate(iso)}`;
}

const AVATAR_TONES = ['bg-indigo-200 text-indigo-800', 'bg-amber-200 text-amber-800', 'bg-emerald-200 text-emerald-800', 'bg-sky-200 text-sky-800'];

const HEALTH_TONES: Record<HealthTone, { dot: string; text: string }> = {
  green: { dot: 'bg-emerald-500', text: 'On track' },
  yellow: { dot: 'bg-amber-400', text: 'Needs attention' },
  red: { dot: 'bg-red-500', text: 'At risk' },
  neutral: { dot: 'bg-slate-300', text: 'Not started' },
};

const CARD_STATUS_LABELS: Record<CategoryCard['status'], string> = {
  completed: 'Finished',
  'in-progress': 'In progress',
  ready: 'Ready to start',
  locked: 'Locked',
};

const URGENCY_TONES: Record<Urgency, string> = {
  overdue: 'bg-red-100 text-red-800 border-red-200',
  today: 'bg-orange-100 text-orange-800 border-orange-200',
  tomorrow: 'bg-amber-100 text-amber-800 border-amber-200',
  thisWeek: 'bg-slate-100 text-slate-700 border-slate-200',
  none: '',
};

const MILESTONE_LABELS: Record<Milestone['status'], string> = {
  completed: 'Complete',
  'in-progress': 'In progress',
  upcoming: 'Upcoming',
};

const PRIMARY_BTN = 'h-11 px-5 rounded-lg bg-indigo-700 text-white text-sm font-semibold hover:bg-indigo-800 disabled:opacity-60 shrink-0 cursor-pointer';
const OUTLINE_BTN = 'h-11 px-5 rounded-lg border border-indigo-700 bg-white text-indigo-700 text-sm font-semibold hover:bg-indigo-50 shrink-0 cursor-pointer inline-flex items-center';
const NEUTRAL_BTN = 'h-11 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm font-semibold hover:bg-slate-50 cursor-pointer';

function HealthDot({ label, tone }: { label: string; tone: HealthTone }) {
  const t = HEALTH_TONES[tone];
  return (
    <div className="flex items-center gap-3" data-testid={`health-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span aria-hidden="true" className={`size-3 rounded-full ${t.dot} shrink-0`} />
      <div className="flex flex-col">
        <span className="text-[13px] text-slate-500">{label}</span>
        <span className="text-sm font-semibold text-slate-900">{t.text}</span>
      </div>
    </div>
  );
}

function RowIcon({ kind, locked = false }: { kind: WorkRow['kind']; locked?: boolean }) {
  const warm = kind === 'follow_up';
  const neutral = kind === 'approval' || locked;
  const wrap = warm ? 'bg-orange-100' : neutral ? 'bg-slate-100' : 'bg-indigo-100';
  const stroke = warm ? '#c2410c' : neutral ? '#475569' : '#4338ca';
  if (locked) {
    return (
      <div className={`size-10 rounded-full ${wrap} flex items-center justify-center shrink-0`}>
        <Lock aria-hidden="true" className="size-4 text-slate-600" />
      </div>
    );
  }
  return (
    <div className={`size-10 rounded-full ${wrap} flex items-center justify-center shrink-0`}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2">
        {kind === 'follow_up' && <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />}
        {(kind === 'category' || kind === 'assigned') && (<><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>)}
        {kind === 'suggestion' && <path d="M20 6 9 17l-5-5" />}
        {kind === 'approval' && (<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" /></>)}
      </svg>
    </div>
  );
}

export function ClientHome({ companyId, companyName, accessToken, currentUser, onLogout, onStartCategory, readOnly = false }: ClientHomeProps) {
  const [data, setData] = useState<ClientHomePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gridCell, setGridCell] = useState<string | null | undefined>(undefined); // undefined = closed
  const [messagesView, setMessagesView] = useState<'closed' | 'list' | 'compose'>('closed');
  const [answerItem, setAnswerItem] = useState<{ id: string; name: string; category: string } | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [snoozeRow, setSnoozeRow] = useState<AssignedRow | null>(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [snoozeReason, setSnoozeReason] = useState('');

  // The focused engagement (PRD §4.7): an explicit switch, else the URL or the stored preference.
  const [engagementId, setEngagementId] = useState<string | null>(() => preferredEngagementIdHint({ userId: currentUser?.id, companyId }));

  const load = useCallback(async (focus: string | null = engagementId) => {
    if (!companyId) return;
    try {
      const query = focus ? `?engagementId=${encodeURIComponent(focus)}` : '';
      const res = await fetch(`${BASE}/client/home/${encodeURIComponent(companyId)}${query}`, { headers: { Authorization: `Bearer ${accessToken}` } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
      setData(body as ClientHomePayload);
      setError(null);
    } catch (e: any) {
      console.error('[ClientHome] load failed:', e);
      setError(e?.message || 'Could not load your project');
    } finally {
      setLoading(false);
    }
  }, [companyId, accessToken, engagementId]);

  useEffect(() => { load(); }, [load]);

  const switchEngagement = (id: string) => {
    writeFocusedEngagementPreference(currentUser?.id, companyId, id);
    setEngagementId(id);
    setLoading(true);
  };

  const openGrid = (cellId: string | null) => {
    try {
      const url = new URL(window.location.href);
      if (cellId) url.searchParams.set('cell', cellId);
      else url.searchParams.delete('cell');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* ignore */ }
    setGridCell(cellId);
  };

  const closeGrid = () => {
    openGrid(null);
    setGridCell(undefined);
    load();
  };

  const confirmSuggestion = async (row: Extract<WorkRow, { kind: 'suggestion' }>) => {
    if (!row.guided) { openGrid(row.cellId); return; }
    setBusyRow(row.id);
    try {
      await saveGuidedAnswer(companyId, row.category, {
        questionId: row.questionId,
        question: row.title,
        answer: row.suggested,
        answeredBy: currentUser?.id,
        answeredByName: currentUser?.name || currentUser?.email,
      }, accessToken);
      toast.success('Thanks, confirmed');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save that');
    } finally {
      setBusyRow(null);
    }
  };

  // MyActionItems' Snooze and Mark Complete, on the home's assigned rows (RESTORE_PLAN R2.2).
  const patchActionItem = async (row: AssignedRow, suffix: 'snooze' | 'complete', body: Record<string, unknown>, done: string) => {
    setBusyRow(row.id);
    try {
      const res = await fetch(`${BASE}/action-items/${encodeURIComponent(companyId)}/${encodeURIComponent(row.configItemId)}/${suffix}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || `Request failed (${res.status})`);
      toast.success(done);
      await load();
      return true;
    } catch (e: any) {
      toast.error(e?.message || 'Could not save that');
      return false;
    } finally {
      setBusyRow(null);
    }
  };

  const markDone = (row: AssignedRow) => patchActionItem(row, 'complete', {}, 'Marked done');

  const openSnooze = (row: AssignedRow) => {
    setSnoozeRow(row);
    setSnoozeDate(row.dueDate ? String(row.dueDate).slice(0, 10) : '');
    setSnoozeReason('');
  };

  const submitSnooze = async () => {
    if (!snoozeRow) return;
    if (!snoozeDate) { toast.error('Pick a new date'); return; }
    const ok = await patchActionItem(snoozeRow, 'snooze', { newDueDate: snoozeDate, snoozeReason: snoozeReason.trim() || null }, `Moved to ${shortDate(snoozeDate)}`);
    if (ok) setSnoozeRow(null);
  };

  const companyLabel = data?.company.name || companyName;
  const firmName = data?.firmName;
  const consultant = data?.team[0]?.name;
  const subtitle = `${data?.engagement?.name || 'Service desk implementation'}${firmName ? ` with ${firmName}` : ''}`;
  const summary = workSummary(data?.work ?? []);
  const overdue = overdueSummary(data?.work ?? []);
  const progress = data?.progress ?? { answered: 0, total: 0 };
  const percent = percentOf(progress);
  const note = data?.statusNote;
  const noteAuthor = note?.authorName || consultant;
  const engagements = data?.engagements ?? [];
  const categories = data?.categories ?? [];
  const locks = lockedCategories(categories);
  const finishedCategories = categories.filter((c) => c.status === 'completed').length;
  const stats = data?.stats;
  const health = data?.health;
  const milestones = data?.milestones ?? [];
  const escalations = data?.escalations;
  const journey = data?.journey ?? null;

  const navUser = {
    id: currentUser.id,
    name: currentUser.name || currentUser.email?.split('@')[0] || 'User',
    email: currentUser.email,
    role: currentUser.activeRole || currentUser.role || 'client',
    avatarUrl: currentUser.avatarUrl,
    isDemo: currentUser.isDemo,
  };

  const renderRow = (row: WorkRow) => {
    let label = '';
    let title = row.title;
    let detail = row.detail ?? '';
    let actions: JSX.Element;
    switch (row.kind) {
      case 'follow_up':
        label = `Question from ${row.askedBy || consultant || 'your consultant'}${row.askedAt ? ` · ${waitingFor(row.askedAt)}` : ''}`;
        title = `“${row.title}”`;
        detail = `About: ${row.about}`;
        actions = <button type="button" className={PRIMARY_BTN} onClick={() => openGrid(row.cellId)}>Reply</button>;
        break;
      case 'assigned': {
        const ago = assignedAgo(row.assignedAt);
        label = `Assigned to you${row.assignedBy ? ` by ${row.assignedBy}` : ''} · ${row.questions} question${row.questions === 1 ? '' : 's'}${row.dueDate ? ` · due ${shortDate(row.dueDate)}` : ` · ${formatMinutes(row.minutes)}`}${ago ? ` · ${ago}` : ''}`;
        detail = row.note ? `Note from ${row.assignedBy || consultant || 'your consultant'}: “${row.note}”` : detail;
        const busy = busyRow === row.id;
        actions = (
          <div className="flex gap-2 shrink-0 flex-wrap">
            <button type="button" className={NEUTRAL_BTN} disabled={busy} onClick={() => openSnooze(row)}>Snooze</button>
            <button type="button" className={NEUTRAL_BTN} disabled={busy} onClick={() => markDone(row)}>{busy ? <Loader2 className="size-4 animate-spin" /> : 'Mark done'}</button>
            <button type="button" className={OUTLINE_BTN} onClick={() => setAnswerItem({ id: row.configItemId, name: row.title, category: row.category })}>Answer</button>
          </div>
        );
        break;
      }
      case 'suggestion':
        label = `Confirm a suggestion · ${formatMinutes(row.minutes)}`;
        actions = (
          <div className="flex gap-2 shrink-0">
            <button type="button" className={NEUTRAL_BTN} onClick={() => openGrid(row.cellId)}>Edit</button>
            <button type="button" className={PRIMARY_BTN} disabled={busyRow === row.id} onClick={() => confirmSuggestion(row)}>
              {busyRow === row.id ? <Loader2 className="size-4 animate-spin" /> : 'Yes, correct'}
            </button>
          </div>
        );
        break;
      case 'category': {
        const lock = locks[row.category];
        label = `${categoryLabel(row.category)} · ${row.unanswered} question${row.unanswered === 1 ? '' : 's'} left · ${formatMinutes(row.minutes)}`;
        if (lock) detail = `${lock}. ${detail}`;
        actions = <button type="button" className={OUTLINE_BTN} disabled={!!lock} title={lock} onClick={() => onStartCategory(row.category)}>Continue</button>;
        break;
      }
      case 'approval':
        label = 'Approval';
        actions = readOnly
          ? <span className={`${OUTLINE_BTN} opacity-50 cursor-not-allowed`} aria-disabled="true">Review</span>
          : <a className={OUTLINE_BTN} href={`/sow/${encodeURIComponent(row.shareToken)}`} target="_blank" rel="noreferrer">Review</a>;
        break;
    }
    const warm = row.kind === 'follow_up';
    const locked = row.kind === 'category' && !!locks[row.category];
    const labelTone = warm ? 'text-orange-700' : row.kind === 'approval' || locked ? 'text-slate-600' : 'text-indigo-700';
    return (
      <article key={row.id} data-testid={`work-row-${row.kind}`} data-locked={locked || undefined} className={`bg-white border ${warm ? 'border-orange-200' : 'border-slate-200'} rounded-xl px-6 py-5 flex items-center gap-5 flex-wrap`}>
        <RowIcon kind={row.kind} locked={locked} />
        <div className="flex-[1_1_320px] min-w-0 flex flex-col gap-1">
          <span className={`text-xs font-semibold uppercase tracking-wide ${labelTone}`}>{label}</span>
          <span className="text-base font-semibold leading-snug text-slate-900 break-words flex items-center gap-2 flex-wrap">
            {title}
            {row.kind === 'assigned' && row.urgency !== 'none' && (
              <span data-testid="urgency-badge" className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${URGENCY_TONES[row.urgency]}`}>{URGENCY_LABELS[row.urgency]}</span>
            )}
          </span>
          {detail && <span className="text-[13px] text-slate-500 break-words">{detail}</span>}
        </div>
        {actions}
      </article>
    );
  };

  // The engine's first next step: "Start here". It opens the grid on that cell, the same place
  // the onboarding's last step pointed at (walk finding L4: the starting point is carried over).
  const start = data?.nextSteps?.[0] ?? null;
  const renderStart = (step: NextStep) => {
    const more = (data?.nextSteps?.length ?? 1) - 1;
    return (
      <article data-testid="next-step" className="bg-indigo-50 border border-indigo-200 rounded-xl px-6 py-5 flex items-center gap-5 flex-wrap">
        <div className="size-9 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0" aria-hidden="true">1</div>
        <div className="flex-[1_1_320px] min-w-0 flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Start here · {categoryLabel(step.category)}</span>
          <span className="text-base font-semibold leading-snug text-slate-900 break-words">{step.text}</span>
          <span className="text-[13px] text-slate-500 break-words">{step.blockName}{more > 0 ? ` · then ${more} more` : ''}</span>
        </div>
        <button type="button" className={PRIMARY_BTN} onClick={() => openGrid(step.cellId)}>Answer</button>
      </article>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <TopNavigation user={navUser} companyId={companyId} companyName={companyLabel} accessToken={accessToken} onLogout={onLogout} hideTodos>
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-8 rounded-lg bg-slate-800 text-white flex items-center justify-center text-sm font-bold shrink-0">{initialsOf(companyLabel)}</div>
          <div className="flex flex-col min-w-0">
            <span className="text-[15px] font-semibold leading-5 truncate">{companyLabel}</span>
            <span className="text-xs text-slate-500 leading-4 truncate">{subtitle}</span>
          </div>
        </div>
      </TopNavigation>

      <main className="w-full max-w-[1040px] mx-auto px-4 sm:px-8 pt-10 pb-16 flex flex-col gap-10">
        {readOnly && (
          <div role="status" data-testid="client-preview-banner" className="bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-xl px-5 py-3 text-sm">
            Preview: this is {companyLabel}&apos;s home as their team sees it. Actions are turned off here.
          </div>
        )}
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="flex flex-col gap-1">
            <h1 className="m-0 text-[28px] font-bold leading-9">{greetingFor(new Date().getHours())}, {readOnly ? 'team' : firstNameOf(currentUser?.name, currentUser?.email)}</h1>
            <p className="m-0 text-[15px] text-slate-600 leading-[22px]">Here is where your project stands and what needs you this week.</p>
          </div>
          {engagements.length > 1 && (
            <div className="flex flex-col gap-1 items-start" data-testid="engagement-switch">
              <span className="text-xs text-slate-500">Showing engagement</span>
              <EngagementSwitcher engagements={engagements.map((e) => ({ id: e.id, name: e.name ?? undefined, status: e.status ?? undefined }))} value={data?.engagement?.id ?? engagementId} onChange={switchEngagement} size="sm" />
            </div>
          )}
        </div>

        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="size-4 animate-spin" /> Loading your project…</div>
        ) : error && !data ? (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-sm text-slate-600 flex items-center justify-between gap-4 flex-wrap">
            <span>We could not load your project just now.</span>
            <button type="button" className={OUTLINE_BTN} onClick={() => { setLoading(true); load(); }}>Try again</button>
          </div>
        ) : (
          // A disabled fieldset turns off every button inside it at once (the preview); links are handled where they render.
          <fieldset disabled={readOnly} className="contents" data-testid="client-home-content">
            <section aria-labelledby="todo-heading" className="flex flex-col gap-4">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <h2 id="todo-heading" className="m-0 text-xl font-semibold leading-7">What we need from you</h2>
                <div className="flex items-baseline gap-4 flex-wrap">
                  {summary.count > 0 && <span className="text-[13px] text-slate-500">{summary.label}</span>}
                  <button type="button" className={OUTLINE_BTN} data-testid="open-grid" onClick={() => openGrid(null)}>Open the Discovery Grid</button>
                </div>
              </div>
              {(overdue.overdue > 0 || overdue.today > 0) && (
                <div role="alert" data-testid="overdue-alert" className="bg-red-50 border border-red-200 text-red-900 rounded-xl px-5 py-3 text-sm">
                  <strong>Action required:</strong>{' '}
                  {[overdue.overdue > 0 ? `${overdue.overdue} overdue` : '', overdue.today > 0 ? `${overdue.today} due today` : ''].filter(Boolean).join(' and ')}.
                </div>
              )}
              {start && renderStart(start)}
              {summary.count === 0 ? (
                !start && (
                  <div className="bg-white border border-slate-200 rounded-xl px-6 py-5 text-[15px] text-slate-600">
                    Nothing right now. {consultant || 'Your consultant'} will let you know.
                  </div>
                )
              ) : (
                <div className="flex flex-col gap-3">{data!.work.map(renderRow)}</div>
              )}
            </section>

            {escalations && escalations.total > 0 && data?.engagement?.id && (
              <section aria-labelledby="urgent-heading" className="flex flex-col gap-4" data-testid="urgent-requests">
                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                  <h2 id="urgent-heading" className="m-0 text-xl font-semibold leading-7">Urgent requests</h2>
                  <span className="text-[13px] text-slate-500">
                    {escalations.open} open{escalations.urgent > 0 ? ` · ${escalations.urgent} urgent` : ''}{escalations.breached > 0 ? ` · ${escalations.breached} past the response window` : ''}
                  </span>
                </div>
                <EscalationInbox accessToken={accessToken} currentUser={currentUser} engagementId={data.engagement.id} compact />
              </section>
            )}

            <section aria-labelledby="status-heading" className="flex flex-col gap-4">
              <h2 id="status-heading" className="m-0 text-xl font-semibold leading-7">Where we are</h2>
              {journey && (
                <div data-testid="journey-banner" className="rounded-xl bg-gradient-to-r from-indigo-700 to-indigo-500 text-white px-6 py-5 flex items-center gap-6 flex-wrap">
                  <div className="flex-[1_1_320px] min-w-0 flex flex-col gap-1">
                    <span className="text-lg font-semibold leading-6">{journey.title}</span>
                    <span className="text-sm text-indigo-100 leading-5">{journey.description}</span>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <span className="text-[28px] font-bold leading-8">{percent}%</span>
                    <span className="text-xs text-indigo-100">{finishedCategories} of {categories.length} area{categories.length === 1 ? '' : 's'} finished</span>
                  </div>
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded-xl p-6 flex gap-8 flex-wrap items-start">
                <div className="flex-[0_0_220px] flex flex-col gap-2">
                  <span className="text-[13px] text-slate-500">Discovery</span>
                  <span className="text-[40px] font-bold leading-[44px]">
                    {progress.answered} <span className="text-lg font-medium text-slate-500">of {progress.total} answered</span>
                  </span>
                  <div role="img" aria-label={`${percent} percent of questions answered`} className="h-2 rounded bg-slate-200 overflow-hidden">
                    <div className="h-2 bg-indigo-700" style={{ width: `${percent}%` }} />
                  </div>
                  <span className="text-[13px] text-slate-500">Build starts when discovery is done</span>
                </div>
                <div className="flex-[1_1_360px] min-w-0 flex flex-col gap-3">
                  {note ? (
                    <>
                      <div className="flex items-center gap-2.5">
                        <div className="size-8 rounded-full bg-indigo-200 text-indigo-800 text-xs font-bold flex items-center justify-center">{initialsOf(noteAuthor || '')}</div>
                        <span className="text-[13px] text-slate-500">Update from {noteAuthor || 'your consultant'}{note.updatedAt ? ` · ${relativeDay(note.updatedAt)}` : ''}</span>
                      </div>
                      {note.text && <p className="m-0 text-base leading-6 whitespace-pre-line">{note.text}</p>}
                      {(note.next || note.then) && (
                        <div className="flex gap-6 flex-wrap text-[13px] text-slate-600">
                          {note.next && <span><strong className="text-slate-900">Next:</strong> {note.next}</span>}
                          {note.then && <span><strong className="text-slate-900">Then:</strong> {note.then}</span>}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="m-0 text-base leading-6 text-slate-600">{consultant || 'Your consultant'} has not posted an update yet. You will see it here when they do.</p>
                  )}
                </div>
              </div>

              {(health || stats) && (
                <div className="flex gap-4 flex-wrap">
                  {health && (
                    <div className="flex-[1_1_280px] bg-white border border-slate-200 rounded-xl p-6 flex flex-col gap-4" data-testid="engagement-health">
                      <span className="text-[13px] font-semibold text-slate-700">Engagement health</span>
                      <div className="flex gap-8 flex-wrap">
                        <HealthDot label="Overall status" tone={health.overall} />
                        <HealthDot label="Timeline confidence" tone={health.timeline} />
                      </div>
                    </div>
                  )}
                  {stats && (
                    <dl className="flex-[1_1_280px] m-0 bg-white border border-slate-200 rounded-xl p-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="quick-stats">
                      {([
                        ['Items', stats.total, 'text-slate-900'],
                        ['Completed', stats.completed, 'text-emerald-700'],
                        ['In progress', stats.inProgress, 'text-indigo-700'],
                        ['Blocked', stats.blocked, stats.blocked > 0 ? 'text-red-700' : 'text-slate-900'],
                      ] as const).map(([label, value, tone]) => (
                        <div key={label} className="flex flex-col gap-0.5">
                          <dt className="text-[13px] text-slate-500">{label}</dt>
                          <dd className={`m-0 text-2xl font-bold leading-8 ${tone}`}>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              )}

              {categories.length > 0 && (
                <div className="flex flex-col gap-3">
                  <h3 className="m-0 text-[15px] font-semibold text-slate-700 leading-[22px]">By area</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((card) => {
                      const action = card.status === 'completed' ? 'Review' : card.status === 'in-progress' ? 'Continue' : 'Start';
                      return (
                        <article key={card.category} data-testid={`category-card-${card.category}`} data-status={card.status} className={`bg-white border rounded-xl p-5 flex flex-col gap-3 ${card.locked ? 'border-slate-200 opacity-70' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-base font-semibold text-slate-900 truncate">{card.label}</span>
                            <span className={`text-xs font-semibold uppercase tracking-wide shrink-0 ${card.status === 'completed' ? 'text-emerald-700' : card.status === 'locked' ? 'text-slate-500' : 'text-indigo-700'}`}>{CARD_STATUS_LABELS[card.status]}</span>
                          </div>
                          <div role="img" aria-label={`${card.percent} percent of ${card.label} questions answered`} className="h-1.5 rounded bg-slate-200 overflow-hidden">
                            <div className={`h-1.5 ${card.status === 'completed' ? 'bg-emerald-600' : 'bg-indigo-700'}`} style={{ width: `${card.percent}%` }} />
                          </div>
                          <span className="text-[13px] text-slate-500">
                            {card.items} item{card.items === 1 ? '' : 's'} · {card.answered} of {card.total} answered{card.status !== 'completed' && card.minutes > 0 ? ` · ${formatMinutes(card.minutes)} left` : ''}
                          </span>
                          {card.locked && card.lockReason ? (
                            <span className="text-[13px] text-slate-600 inline-flex items-center gap-1.5"><Lock aria-hidden="true" className="size-3.5" /> {card.lockReason}</span>
                          ) : (
                            <button type="button" className={`${NEUTRAL_BTN} h-9 self-start`} onClick={() => onStartCategory(card.category)}>{action}</button>
                          )}
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}

              {milestones.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col gap-3" data-testid="milestones">
                  <span className="text-[13px] font-semibold text-slate-700">Milestones</span>
                  <ul className="m-0 p-0 list-none divide-y divide-slate-100">
                    {milestones.map((m) => (
                      <li key={m.id} className="flex items-center gap-3 py-2.5">
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={m.status === 'completed' ? '#059669' : m.status === 'in-progress' ? '#4338ca' : '#cbd5e1'} strokeWidth="2">
                          {m.status === 'completed' ? <path d="M20 6 9 17l-5-5" /> : <circle cx="12" cy="12" r="9" />}
                        </svg>
                        <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{m.title}</span>
                        <span className="text-xs text-slate-500">{m.done} of {m.total}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${m.status === 'completed' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : m.status === 'in-progress' ? 'text-indigo-700 border-indigo-200 bg-indigo-50' : 'text-slate-500 border-slate-200 bg-slate-50'}`}>{MILESTONE_LABELS[m.status]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section aria-labelledby="team-heading" className="flex flex-col gap-4">
              <h2 id="team-heading" className="m-0 text-xl font-semibold leading-7">Your consulting team</h2>
              <div className="bg-white border border-slate-200 rounded-xl p-6 flex gap-6 flex-wrap items-center">
                {(data?.team ?? []).length === 0 && (
                  <span className="flex-[1_1_260px] text-[15px] text-slate-600">Your consulting team has not been assigned yet.</span>
                )}
                {(data?.team ?? []).map((m, i) => (
                  <div key={m.userId} className="flex-[1_1_260px] flex items-center gap-3.5 min-w-0">
                    <div className={`size-12 rounded-full ${AVATAR_TONES[i % AVATAR_TONES.length]} text-[15px] font-bold flex items-center justify-center shrink-0`}>{initialsOf(m.name)}</div>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-base font-semibold">{m.name}</span>
                      <span className="text-[13px] text-slate-500">{m.roleLabel}{m.roleLabel === 'Lead consultant' ? ' · your day-to-day contact' : ''}</span>
                      <a href={`mailto:${m.email}`} className="text-[13px] text-indigo-700 hover:text-indigo-800 truncate">{m.email}</a>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3 flex-wrap">
                  <button type="button" className={`${OUTLINE_BTN} inline-flex items-center gap-2`} data-testid="open-messages" onClick={() => setMessagesView('list')}>
                    Open messages
                    {(data?.messages?.unread ?? 0) > 0 && (
                      <span className="rounded-full bg-orange-500 text-white text-xs font-bold px-2 py-0.5" aria-label={`${data!.messages!.unread} unread`}>{data!.messages!.unread}</span>
                    )}
                  </button>
                  <button type="button" className={`${PRIMARY_BTN} inline-flex items-center gap-2`} onClick={() => setMessagesView('compose')}>
                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4z" /></svg>
                    Send a message
                  </button>
                </div>
              </div>
            </section>

            {(data?.recent ?? []).length > 0 && (
              <section aria-labelledby="done-heading" className="flex flex-col gap-3">
                <h2 id="done-heading" className="m-0 text-[15px] font-semibold text-slate-600 leading-[22px]">Recently done</h2>
                <ul className="m-0 p-0 list-none flex flex-col gap-2 text-sm text-slate-600">
                  {data!.recent.map((e) => (
                    <li key={e.id} className="flex gap-2.5 items-center">
                      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg>
                      <span>{e.text} · {shortDate(e.at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </fieldset>
        )}
      </main>

      {gridCell !== undefined && (
        <ProgressGridModal
          isOpen
          onClose={closeGrid}
          companyId={companyId}
          companyName={companyLabel}
          accessToken={accessToken}
          currentUser={navUser}
          initialCellId={gridCell}
        />
      )}

      {messagesView !== 'closed' && (
        <MessageCenter
          userId={currentUser.id}
          userName={navUser.name}
          userEmail={currentUser.email}
          userRole={navUser.role}
          companyId={companyId}
          projectId={projectId}
          publicAnonKey={publicAnonKey}
          accessToken={accessToken}
          initialComposing={messagesView === 'compose'}
          onClose={() => { setMessagesView('closed'); load(); }}
        />
      )}

      {snoozeRow && (
        <Dialog open onOpenChange={(open) => { if (!open) setSnoozeRow(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Snooze “{snoozeRow.title}”</DialogTitle>
              <DialogDescription>Pick the new date you will have this done by. {snoozeRow.assignedBy || consultant || 'Your consultant'} will see the change.</DialogDescription>
            </DialogHeader>
            <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submitSnooze(); }}>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-slate-800">New date</span>
                <input type="date" required value={snoozeDate} onChange={(e) => setSnoozeDate(e.target.value)} className="h-10 rounded-lg border border-slate-300 px-3" data-testid="snooze-date" />
              </label>
              <label className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-slate-800">Reason <span className="font-normal text-slate-500">(optional)</span></span>
                <textarea value={snoozeReason} onChange={(e) => setSnoozeReason(e.target.value)} rows={3} className="rounded-lg border border-slate-300 px-3 py-2" data-testid="snooze-reason" />
              </label>
              <DialogFooter>
                <button type="button" className={NEUTRAL_BTN} onClick={() => setSnoozeRow(null)}>Cancel</button>
                <button type="submit" className={PRIMARY_BTN} disabled={busyRow === snoozeRow.id}>{busyRow === snoozeRow.id ? <Loader2 className="size-4 animate-spin" /> : 'Snooze'}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {answerItem && (
        <Dialog open onOpenChange={(open) => { if (!open) { setAnswerItem(null); load(); } }}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{answerItem.name}</DialogTitle>
              <DialogDescription>Answer what you can. Your consultant sees your answers right away.</DialogDescription>
            </DialogHeader>
            <DiscoveryQuestionsPanel
              configItemId={answerItem.id}
              configItemName={answerItem.name}
              companyId={companyId}
              sowId={data?.engagement?.id}
              accessToken={accessToken}
              isConsultant={false}
              currentUser={currentUser}
              configItemCategory={answerItem.category}
              consultingFirmId={currentUser?.consultingFirmId || currentUser?.firmId}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
